/**
 * "Vem aí" no painel: o `upcoming_countdowns.dart`.
 *
 * A fatia que fica entre "Neste dia" e a caixa de cartas na ordem do painel de
 * lá (`dashboard_screen.dart:400`), e mais uma das poucas sem `so:` — não
 * desliga nos ajustes. São as três próximas datas dos sessenta dias à frente,
 * cada uma num cartão de 268 com a contagem regressiva andando: dias, horas,
 * minutos, e os segundos quando falta menos de um dia.
 *
 * Não é a `PreviaDaAgenda`, que também vive no painel: aquela é o
 * `CalendarPreview`, uma lista de três linhas com "Hoje/Amanhã/Em N dias" e
 * janela de trinta dias, e fica desligável em `calendar`. As duas convivem lá
 * do mesmo jeito.
 *
 * As palavras miúdas ficam como estão lá — `'no dates planned yet'`,
 * `'N upcoming dates'`, `'Calendar'`, `'AGORA'`, `'HOJE'`, `'TOMORROW'`,
 * `'ANNIVERSARY'`, `'days'`, `'hrs'`, `'min'`, `'sec'` —, e o rodapé em
 * português, que é como o outro app mostra.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **O "Calendar" do canto não é um segundo botão.** Lá o cabeçalho tem um
 *   `TextButton` que empurra `/calendar` e a seção inteira também. Um `<a>`
 *   dentro de outro `<a>` não existe em HTML, e como os dois vão para o mesmo
 *   lugar o que sobra é a palavra: a seção é o link, e "Calendar" é o rótulo
 *   dele no canto.
 * - **O selo do tipo é um traço, e não um emoji.** A casa não põe emoji em
 *   tela; o `iconeDoCompromisso` diz qual desenho representa cada tipo.
 * - **O `_TickSeparator` não pisca sozinho.** Lá é um controlador repetindo de
 *   900 em 900; aqui é uma animação de CSS, que o `prefers-reduced-motion`
 *   apaga junto com todas as outras.
 * - **Cormorant vai até 700.** O `cormorantBlack` do número gigante ao fundo
 *   não tem arquivo aqui: a casa carrega Regular, SemiBold e Bold. Fica o Bold,
 *   que a 4,5% de opacidade ninguém distingue.
 */

import { useMemo } from 'react';
import {
  COLECOES,
  JANELA_DE_VEM_AI,
  compromissoDeMapa,
  corDoCompromisso,
  diaEMes,
  iconeDoCompromisso,
  proximosCompromissos,
  traduzir,
  usarColecao,
  usarContagem,
  type Compromisso,
} from '@nosso-sempre/shared';
import { Icone } from './Icone';
import { Secao, SetaDaSecao } from './Secao';
import './previas.css';

/** O `.take(3)` de lá. */
const QUANTOS = 3;

export function PreviaDeVemAi() {
  const docs = usarColecao<Record<string, unknown>>(COLECOES.agenda);

  const proximos = useMemo(() => {
    const compromissos = docs.map((d) => compromissoDeMapa(d.id, d.dados));
    return proximosCompromissos(compromissos, JANELA_DE_VEM_AI).slice(0, QUANTOS);
  }, [docs]);

  const quantos = proximos.length;

  return (
    <Secao
      icone="calendario"
      cor="var(--ambar)"
      titulo={traduzir('Vem aí')}
      legenda={
        quantos === 0
          ? traduzir('no dates planned yet')
          : traduzir('{quantos} upcoming {v}', {
              quantos,
              v: quantos === 1 ? traduzir('date') : traduzir('dates'),
            })
      }
      para="/calendario"
      ponta={<span className="previa-vemai-atalho">{traduzir('Calendar')}</span>}
    >
      {quantos === 0 ? (
        <p className="previa-convite">
          <Icone nome="mais-circulo" tamanho={18} />
          <span className="previa-vemai-chamado">
            {traduzir('Toque para marcar a próxima data especial')}
          </span>
          <SetaDaSecao />
        </p>
      ) : (
        <ul className="previa-vemai">
          {proximos.map((c) => (
            // A repetição traz o mesmo `id` em datas diferentes, então a data
            // entra na chave — sem ela o React descartaria as ocorrências.
            <li key={`${c.id}-${c.quando.getTime()}`}>
              <FichaDaContagem compromisso={c} />
            </li>
          ))}
        </ul>
      )}
    </Secao>
  );
}

/** O `_CountdownEventCard`: 268 de largura, a contagem no meio, a barra no pé. */
function FichaDaContagem({ compromisso }: { compromisso: Compromisso }) {
  const c = usarContagem(compromisso.quando);
  const aniversario = compromisso.tipo === 'anniversary';

  return (
    <article
      className={c.iminente ? 'previa-vemai-ficha iminente' : 'previa-vemai-ficha'}
      style={{ ['--cor-tipo' as string]: corDoCompromisso(compromisso.tipo) }}
    >
      <span className="previa-vemai-fio" aria-hidden="true" />

      {/* O `_WatermarkDays`: o número de dias enorme e quase apagado ao fundo. */}
      <span className="previa-vemai-marca" aria-hidden="true">
        {c.dias}
      </span>

      {c.urgencia ? (
        <span className="previa-vemai-urgencia">
          <span className="previa-vemai-pontinho" aria-hidden="true" />
          {c.urgencia}
        </span>
      ) : null}

      <div className="previa-vemai-miolo">
        <header className="previa-vemai-topo">
          <span className="previa-vemai-selo" aria-hidden="true">
            <Icone nome={iconeDoCompromisso(compromisso.tipo)} tamanho={19} />
          </span>

          <div className="previa-vemai-dizeres">
            <h3 className="previa-vemai-nome">{compromisso.titulo.toUpperCase()}</h3>
            <p className="previa-vemai-data">
              <Icone nome="calendario" tamanho={10} />
              <span>{diaEMes(compromisso.quando)}</span>
              {aniversario ? (
                <>
                  <span className="previa-vemai-separador" aria-hidden="true" />
                  <span className="previa-vemai-tipo">{traduzir('ANNIVERSARY')}</span>
                </>
              ) : null}
            </p>
          </div>
        </header>

        <div className="previa-vemai-unidades">
          <Capsula valor={c.dias} nome="days" destaque />
          <Piscada />
          <Capsula valor={c.horas} nome="hrs" />
          <Piscada />
          <Capsula valor={c.minutos} nome="min" />
          {c.mostraSegundos ? (
            <>
              <Piscada />
              <Capsula valor={c.segundos} nome="sec" viva />
            </>
          ) : null}
        </div>

        <span className="previa-vemai-tracejado" aria-hidden="true" />

        <footer className={c.perto ? 'previa-vemai-pe perto' : 'previa-vemai-pe'}>
          <span className="previa-vemai-trilho">
            <span className="previa-vemai-cheio" style={{ width: `${c.fracao * 100}%` }}>
              <span className="previa-vemai-cabeca" aria-hidden="true" />
            </span>
          </span>
          <span className="previa-vemai-falta">
            {/* Lá é o raio e a ampulheta; a biblioteca da casa tem a chama e o
                relógio, que são os mesmos dois estados. */}
            <Icone nome={c.perto ? 'chama' : 'relogio'} tamanho={11} />
            {c.falta}
          </span>
        </footer>
      </div>
    </article>
  );
}

/** O `_TimeCapsule`: o número de dois dígitos e a palavra miúda embaixo. */
function Capsula({
  valor,
  nome,
  destaque,
  viva,
}: {
  valor: number;
  nome: string;
  destaque?: boolean;
  viva?: boolean;
}) {
  const classes = ['previa-vemai-capsula'];
  if (destaque) classes.push('destaque');
  if (viva) classes.push('viva');

  return (
    <span className={classes.join(' ')}>
      {/* A chave troca a cada número para o CSS refazer a entrada, que é o
          `AnimatedSwitcher` de lá em duzentos e oitenta milissegundos. */}
      <span key={valor} className="previa-vemai-valor">
        {String(valor).padStart(2, '0')}
      </span>
      <span className="previa-vemai-unidade">{nome}</span>
      {viva ? <span className="previa-vemai-risco" aria-hidden="true" /> : null}
    </span>
  );
}

/** O `_TickSeparator`: os dois pontinhos que piscam entre as cápsulas. */
function Piscada() {
  return <span className="previa-vemai-piscada" aria-hidden="true" />;
}
